/**
 * CRM — Anagrafica clienti (BE v1.20.11+37). Lista per-portafoglio con ricerca,
 * badge tipo (Azienda/Privato) e badge "Tenant" per i clienti B2B reciproci.
 * Il dettaglio (contatti + collegamenti tenant) è in /clients/$clientId.
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Badge, Button, Group, Text, rem } from '@mantine/core';
import { IconAddressBook, IconLayoutGrid, IconPlus } from '@tabler/icons-react';
import { mw } from '@/shared/mobile/tokens';
import { useState } from 'react';
import type { ResourceColumn } from '@/shared/components/DataTable/types';
import { ResourceListPage } from '@/shared/components/ResourceListPage';
import { ClientFormModal } from '@/features/clients/ClientFormModal';
import { clientDisplayName, type ClientRow } from '@/features/clients/api';
import { DesktopClients } from '@/features/clients/desktop/DesktopClients';

export const Route = createFileRoute('/_app/clients/')({
  validateSearch: (s: Record<string, unknown>): { view?: 'table' } => ({ view: s.view === 'table' ? 'table' : undefined }),
  component: ClientsPage,
});

/** Default = card-grid brand MaeDesign; `?view=table` = ResourceListPage completa. */
function ClientsPage() {
  const { view } = Route.useSearch();
  return view === 'table' ? <ClientsListPage /> : <DesktopClients />;
}

const COLUMNS: ResourceColumn<ClientRow>[] = [
  {
    accessor: 'displayName',
    title: 'Cliente',
    sortable: false,
    filterable: false,
    render: (row) => (
      <Group gap={8} wrap="nowrap">
        <Text size="sm" fw={600}>{clientDisplayName(row)}</Text>
        {row.clientType === 'private'
          ? <Badge size="xs" variant="light" color="grape">Privato</Badge>
          : <Badge size="xs" variant="light" color="blue">Azienda</Badge>}
        {row.linkedTenant && (
          <Badge size="xs" variant="light" color="teal">Sulla piattaforma</Badge>
        )}
      </Group>
    ),
  },
  {
    accessor: 'fiscal',
    title: 'P.IVA / CF',
    sortable: false,
    filterable: false,
    width: 160,
    render: (row) => <Text size="sm">{row.vatNumber ?? row.taxId ?? '—'}</Text>,
  },
  { accessor: 'city', title: 'Città', width: 140, render: (row) => <Text size="sm">{row.city ?? '—'}</Text> },
  { accessor: 'email', title: 'Email', render: (row) => <Text size="sm">{row.email ?? '—'}</Text> },
  { accessor: 'phoneNumber', title: 'Telefono', width: 140, render: (row) => <Text size="sm">{row.phoneNumber ?? '—'}</Text> },
  { accessor: 'industryCode', title: 'Settore', width: 110, render: (row) => <Text size="sm">{row.industryCode ?? '—'}</Text> },
];

function ClientsListPage() {
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <>
      <ResourceListPage<ClientRow>
        title="Clienti"
        description="Anagrafica CRM del portafoglio: dati fiscali, contatti e collegamenti alle società del gruppo."
        type="clients"
        columns={COLUMNS}
        searchField="organization"
        include={['linkedTenant']}
        onRowClick={(row) => void navigate({ to: `/clients/${row.id}` })}
        emptyMessage="Nessun cliente in anagrafica"
        emptyDescription="Crea il primo cliente con il bottone «Nuovo cliente»: entrerà nel portafoglio di questa azienda come Lead."
        emptyIcon={IconAddressBook}
        toolbarRight={
          <Group gap={10} wrap="nowrap">
            <button className="mw-pressable" onClick={() => void navigate({ to: '/clients' })} style={{ display: 'flex', alignItems: 'center', gap: 8, height: rem(40), padding: '0 16px', borderRadius: 10, cursor: 'pointer', border: `1px solid ${mw.line}`, background: mw.surface, color: mw.ink900, fontSize: 'var(--fs-body)', fontWeight: 600, fontFamily: 'inherit' }}><IconLayoutGrid size={16} /> Vista a schede</button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
              Nuovo cliente
            </Button>
          </Group>
        }
      />
      <ClientFormModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={(id) => { if (id) void navigate({ to: `/clients/${id}` }); }}
      />
    </>
  );
}
